/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { baseApiService } from './base'

export interface Tag {
  id: number
  name: string
  type?: 'SYSTEM' | 'USER'
  propertyType?: 'SYSTEM' | 'USER'
  description?: string
  createdAt?: string
  updatedAt?: string
}

export interface TagPickerResponse {
  options: Tag[]
  hasMore: boolean
}

export interface TagListParams {
  name?: string
  pageStart?: number
  pageSize?: number
}

export interface AssignTagsRequest {
  fsiIds: number[]
  tagIdsToAdd?: number[] | null
  tagIdsToRemove?: number[] | null
}

/**
 * Get all tags for table/list view
 */
export async function getListForTable(filter?: TagListParams): Promise<Tag[]> {
  const response = await baseApiService.fetch<Tag[]>('/api/v1/tags', { params: filter as any })
  return response
}

/**
 * Get all tags for picker (non-paginated)
 */
export async function getAllForPicker(): Promise<Tag[]> {
  const response = await baseApiService.fetch<Tag[]>('/api/v1/tags/getall')
  return response
}

/**
 * Get tags with pagination for async picker
 */
export async function getOptionsForAsyncPicker(search?: string, pageStart: number = 0): Promise<TagPickerResponse> {
  const params: any = {
    name: search || undefined,
    pageStart,
    pageSize: 25
  }

  const response = await baseApiService.fetch<any>('/api/v2/tags/picker', { params })

  return {
    options: response.elements || response.options || [],
    hasMore: response.hasMore !== false
  }
}

/**
 * Get tags assigned to a specific file/folder
 */
export async function getTagsByFsi(fsiId: number): Promise<Tag[]> {
  const response = await baseApiService.fetch<Tag[]>(`/api/v1/tags/document/${fsiId}`)
  return response
}

/**
 * Assign/unassign tags to files
 */
export async function assignTagsToFsis(
  fsiIds: number | number[],
  addTagIds?: number[],
  removeTagIds?: number[]
): Promise<void> {
  const reqData = {
    fsiIds: Array.isArray(fsiIds) ? fsiIds : [fsiIds],
    tagIdsToAdd: addTagIds || null,
    tagIdsToRemove: removeTagIds || null
  }

  await baseApiService.fetch<void>('/api/v1/tags/documents', {
    method: 'PUT',
    body: reqData as any
  })
}

/**
 * Create a new tag
 */
export async function createTag(tag: Partial<Tag>): Promise<Tag> {
  const response = await baseApiService.fetch<Tag>('/api/v1/tags', {
    method: 'POST',
    body: tag
  })
  return response
}

/**
 * Update an existing tag
 */
export async function updateTag(id: number, data: Partial<Tag>): Promise<Tag> {
  const response = await baseApiService.fetch<Tag>(`/api/v1/tags/${id}`, {
    method: 'PUT',
    body: data
  })
  return response
}

/**
 * Delete a tag
 */
export async function deleteTag(id: number): Promise<void> {
  await baseApiService.fetch<void>(`/api/v1/tags/${id}`, {
    method: 'DELETE'
  })
}

/**
 * Get tag by ID
 */
export async function getTagById(id: number): Promise<Tag> {
  const response = await baseApiService.fetch<Tag>(`/api/v1/tags/${id}`)
  return response
}

/**
 * Get tag options with filters
 */
export async function getTagOptions(filter?: Record<string, any>): Promise<Tag[]> {
  const response = await baseApiService.fetch<Tag[]>('/api/tags/options/', { params: filter as any })
  return response
}
